import { useAuthStore } from '@/stores/authStore'
import { PLATFORM_ROLES } from '@/types/auth'

/**
 * Resolve a notification's action_url to a route that exists in the current
 * session scope.
 *
 * Backend notification producers don't always know the UI scope prefix. Approval
 * deep-links were historically minted as `/approvals/{id}` (no scope), which is a
 * dead route — the real pages are `/tenant/approvals/{id}` and
 * `/platform/approvals/{id}`. Clicking such a notice 404s.
 *
 * We resolve the correct prefix at click time from the active session: a user
 * acting inside a tenant (tenantId set) reads the tenant route; the platform
 * console (no active tenant, platform role) reads the platform route. We strip
 * any pre-existing scope prefix first, so this also re-targets a link that was
 * minted for the other scope.
 *
 * Only the `approvals` section is re-scoped — every other action_url is returned
 * verbatim so we never rewrite a link the producer authored correctly.
 */
export function resolveActionUrl(actionUrl: string): string {
  // External / non-app URLs (http…, mailto…) pass through untouched.
  if (!actionUrl.startsWith('/')) return actionUrl

  // Strip an existing /tenant or /platform scope prefix, if any.
  const unscoped = actionUrl.replace(/^\/(tenant|platform)(?=\/)/, '')

  // Only normalize approval deep-links; leave all other routes as authored.
  if (!/^\/approvals(\/|$|\?)/.test(unscoped)) return actionUrl

  const { user } = useAuthStore.getState()
  // Derived from the same PLATFORM_ROLES the router uses — see
  // types/auth.ts. Was a 3-role literal (missing platform_auditor and the
  // 15 ops/support/finance roles), so deep links resolved to the wrong plane.
  const isPlatformConsole = !user?.tenantId && !!user?.role && PLATFORM_ROLES.includes(user.role)

  const scope = isPlatformConsole ? 'platform' : 'tenant'
  return `/${scope}${unscoped}`
}
